// What the Import files button does once pressed: hand the one file to the planner (Edit in
// planner), or save every file that's on through the existing mutations (importSave.ts).
// Kept out of the component so the dialog only sequences; the decisions are in utils/import.

import graphClient from '../../utils/graphql/graph_client';
import { CreateRecording } from '../../utils/graphql/recorded-trail-graph';
import { CreateHikeWithPlannedTrails } from '../../utils/graphql/hikes-graph';
import { CreateWaypointGroup, CreateWaypoints } from '../../utils/graphql/waypoints-graph';
import { getElevationData } from '../../utils/helpers/HiikerElevationApiHelper';
import { snapPoints } from '../../utils/models/Graphooper';
import { MarkerFeature, MarkerGeometry, MarkerProperties } from '../../utils/models/MarkerFeature';
import { generateID } from '../../utils/helpers/IDGenerator';
import { WAYPOINT_ROUTE_PROFILE, type WaypointRouter } from '../../utils/import/importWaypointRoute';
import { counts, countsTotal, routeStatus, willImport, type ImportCounts, type ImportScreenState } from '../../utils/import/importScreenState';
import { plannedDays, waypointsToAdd } from '../../utils/import/importPayloads';
import {
  importTitle,
  noRouteFailure,
  saveImports,
  type ImportCreated,
  type ImportSaveDeps,
  type ImportSavedResult
} from '../../utils/import/importSave';
import { handoffFile, plannerHandoff, storeImportResult, storePlannerHandoff } from '../../utils/import/importPlannerHandoff';
import type { ImportPreview } from '../../utils/import/importTypes';
import type { Translate } from '../../utils/import/importTexts';

export type { ImportSavedResult };

export type ImportOutcome =
  /**
   * The Edit in planner file went to the planner. `saved` is the other files' result when it
   * was handed to a planner already open (no page change); when the page changes to the
   * planner, the result travels in sessionStorage and the planner shows it (storeImportResult).
   */
  | { kind: 'openPlanner'; saved?: ImportSavedResult }
  | { kind: 'plannerFailed' }
  | ({ kind: 'saved' } & ImportSavedResult);

export const PLANNER_URL = '/hike-planner';

/** The counts of what was actually created, for the result. */
export function createdCounts(created: ImportCreated[]): ImportCounts {
  return {
    recordings: created.filter(c => c.destination === 'recordings').length,
    plannedHikes: created.filter(c => c.destination === 'plannedHikes').length,
    waypointGroups: created.filter(c => c.destination === 'waypoints').length,
    editInPlanner: false
  };
}

const snapMarker = (coord: number[]): MarkerFeature =>
  new MarkerFeature(new MarkerGeometry([...coord]), new MarkerProperties(generateID(), true, Date.now().toString(), 0));

/**
 * One leg of a hike from waypoints, routed in the background by the dialog's
 * WaypointRouteRunner exactly as the planner routes a snapped segment
 * (polylines.ts `createPolylineGeoJson` → Graphooper `snapPoints`), with GraphHopper's own
 * elevation on every point. Throws when GraphHopper can't be reached or finds no route.
 */
export const plannerRoute: WaypointRouter = async (start, end) =>
  (await snapPoints(snapMarker(start), snapMarker(end), WAYPOINT_ROUTE_PROFILE)).lineStringGeometry.coordinates;

export function saveDeps(t: Translate): ImportSaveDeps {
  return {
    request: (document, variables) => graphClient.request(document as any, variables),
    elevation: coords => getElevationData(coords),
    mutations: {
      createRecording: CreateRecording,
      createHike: CreateHikeWithPlannedTrails,
      createWaypoints: CreateWaypoints,
      createWaypointGroup: CreateWaypointGroup
    },
    texts: {
      defaultTitle: t('import_files.default_title'),
      unnamedWaypoint: t('waypoint_dialog.waypoint_options_dialog_module.imported_waypoint'),
      unnamedHighlight: (n: number) => t('import_files.imported_highlight', { n })
    }
  };
}

/** Saves every importing file except the Edit in planner one, and sums up what happened. */
async function saveOthers(s: ImportScreenState, deps: ImportSaveDeps, handedOff: number): Promise<ImportSavedResult> {
  const importing = s.entries.filter(e => willImport(e) && e.settings.choice !== 'editInPlanner');
  const { created, failures: saveFailures } = await saveImports(importing, deps);
  // A hike from waypoints with no route isn't imported, but the result still says why.
  const unrouted = s.entries
    .filter(e => e.preview && !e.preview.error && e.settings.choice === 'fromWaypoints' && routeStatus(e) === 'failed')
    .map(noRouteFailure);
  const failures = [...saveFailures, ...unrouted];
  return { created, failures, counts: createdCounts(created), notImported: s.entries.length - handedOff - created.length };
}

/**
 * The button. With an Edit in planner file, every other importing file is saved first, then
 * that one goes to the planner — straight into a planner that's already open, or through
 * sessionStorage to /hike-planner. The hand-off is prepared before anything is saved, so a
 * browser that won't hold it fails before any file is created.
 */
export async function runImport(
  s: ImportScreenState,
  t: Translate,
  deps: ImportSaveDeps = saveDeps(t),
  navigate: (url: string) => void = url => window.location.assign(url),
  storage: Pick<Storage, 'setItem'> = window.sessionStorage
): Promise<ImportOutcome> {
  if (!counts(s).editInPlanner) return { kind: 'saved', ...(await saveOthers(s, deps, 0)) };

  const entry = s.entries.find(e => willImport(e) && e.settings.choice === 'editInPlanner');
  const p = entry?.preview as ImportPreview | undefined;
  if (!entry || !p) return { kind: 'plannerFailed' };
  const handoff = plannerHandoff(
    importTitle(entry.name, t('import_files.default_title')),
    plannedDays(p, { ...entry.settings, joinDays: false }),
    waypointsToAdd(p, entry.settings)
  );
  const others = countsTotal(counts(s)) > 0 || s.entries.some(e => e.settings.choice === 'fromWaypoints' && routeStatus(e) === 'failed');
  // Already in the planner: import into it here instead of leaving the plan being edited.
  const hook = window.hiikerPlannerImport;
  const direct = hook ? handoffFile(handoff) : null;
  if (hook && direct) {
    const saved = others ? await saveOthers(s, deps, 1) : undefined;
    await hook(direct);
    return saved ? { kind: 'openPlanner', saved } : { kind: 'openPlanner' };
  }
  if (!storePlannerHandoff(handoff, storage)) return { kind: 'plannerFailed' };
  if (others) {
    // The page changes, so the planner shows the result (openImportFiles `showStoredImportResult`).
    storeImportResult(await saveOthers(s, deps, 1), storage);
  }
  navigate(PLANNER_URL);
  return { kind: 'openPlanner' };
}
